import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import { authAPI, userAPI } from './services/api';

// Import components
import LandingPage from './components/Landing/LandingPage';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import ForgotPassword from './components/Auth/ForgotPassword';
import TransactionHistory from './components/Dashboard/TransactionHistory';
import CreditCardDetails from './components/Dashboard/CreditCardDetails';
import MoneyTransfer from './components/Dashboard/MoneyTransfer';
import SpendingChart from './components/Dashboard/SpendingChart';

// Dashboard with Tab Navigation
const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [user, setUser] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  React.useEffect(() => {
    const fetchUserData = async () => {
      const currentUser = authAPI.getCurrentUser();

      if (!currentUser) {
        navigate('/login');
        return;
      }

      setUser(currentUser);

      try {
        const accountsData = await userAPI.getUserAccounts(currentUser.id);
        setAccounts(accountsData.accounts || []);
      } catch (err) {
        console.error('Failed to fetch accounts:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleLogout = async () => {
    await authAPI.logout();
    navigate('/');
  };

  if (loading || !user) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const totalBalance = accounts.reduce((sum, acc) => sum + parseFloat(acc.balance), 0);

  return (
    <div className="dashboard-layout">
      {/* Header */}
      <header className="dashboard-main-header">
        <div className="header-content">
          <h1>DigiBank</h1>
          <p className="user-info">Welcome, {user.full_name || user.username}</p>
        </div>
        <button onClick={handleLogout} className="btn btn-danger">
          Logout
        </button>
      </header>
      {/* Tab Navigation */}
      <nav className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <span className="tab-icon">📊</span>
          Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'transactions' ? 'active' : ''}`}
          onClick={() => setActiveTab('transactions')}
        >
          <span className="tab-icon">💳</span>
          Transactions
        </button>
        <button
          className={`tab-button ${activeTab === 'cards' ? 'active' : ''}`}
          onClick={() => setActiveTab('cards')}
        >
          <span className="tab-icon">💎</span>
          Credit Cards
        </button>
        <button
          className={`tab-button ${activeTab === 'transfer' ? 'active' : ''}`}
          onClick={() => setActiveTab('transfer')}
        >
          <span className="tab-icon">💸</span>
          Transfer
        </button>
        <button
          className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          <span className="tab-icon">📈</span>
          Analytics
        </button>
      </nav>

      {/* Tab Content */}
      <main className="dashboard-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="balance-summary card">
              <h2>Total Balance</h2>
              <p className="balance-amount">${totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              <p className="balance-subtitle">Across {accounts.length} account{accounts.length !== 1 ? 's' : ''}</p>
            </div>

            <div className="accounts-grid">
              {accounts.map((account) => (
                <div key={account.id} className="account-card card">
                  <div className="account-header">
                    <h3>{account.account_type.charAt(0).toUpperCase() + account.account_type.slice(1)}</h3>
                    <span className="account-status">Active</span>
                  </div>
                  <p className="account-number">
                    <span className="label">Account:</span> {account.account_number}
                  </p>
                  <p className="account-balance">
                    ${parseFloat(account.balance).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="account-currency">{account.currency}</p>
                </div>
              ))}
            </div>

            <div className="card personal-info">
              <h2>Personal Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Email</span>
                  <span className="info-value">{user.email}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Mobile</span>
                  <span className="info-value">{user.mobile || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Address</span>
                  <span className="info-value">{user.address || 'Not provided'}</span>
                </div>
                <div className="info-item vulnerable">
                  <span className="info-label">SSN ⚠️</span>
                  <span className="info-value danger">{user.ssn || 'Not provided'}</span>
                </div>
              </div>
            </div>

            <div className="card vulnerability-demo">
              <h3>🔓 Vulnerability Demonstration</h3>
              <p>This application has intentional security flaws. Try these exploits:</p>
              <ul className="vulnerability-list">
                <li>
                  <strong>BOLA:</strong> Access other users' data by changing the user ID in the URL
                  <code>GET /api/users/2</code>
                </li>
                <li>
                  <strong>SQL Injection:</strong> Try injecting SQL in transaction queries
                  <code>?accountId=1 OR 1=1--</code>
                </li>
                <li>
                  <strong>Privilege Escalation:</strong> Update your role to admin via mass assignment
                  <code>PUT /api/users/{user.id} {JSON.stringify({role: 'admin'})}</code>
                </li>
                <li>
                  <strong>Exposed Secrets:</strong> View configuration and secrets
                  <code>GET /api/debug/config</code>
                </li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'transactions' && <TransactionHistory />}
        {activeTab === 'cards' && <CreditCardDetails />}
        {activeTab === 'transfer' && <MoneyTransfer />}
        {activeTab === 'analytics' && <SpendingChart />}
      </main>
    </div>
  );
};

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}

export default App;
